import { DialogActions, DialogContent, Grid, Stack } from "@mui/material";
import { MutationStatus, useQuery } from "@tanstack/react-query";
// import dayjs from "dayjs";
import dayjs from "dayjs";
import { Form as FormikForm, FormikProvider, useFormik } from "formik";
import { Briefcase, Save } from "lucide-react";
import { FC, useMemo } from "react";
import * as yup from "yup";

import { getHubspotLenders } from "api/hubspot";
import { hubspotKeys } from "api/hubspot/queries";
import { HubspotLenderCreditBureauEnum } from "api/hubspot/types";
import {
  LoanApplicationAccountType,
  LoanApplicationCreditInquiry,
  LoanApplicationStatus,
} from "api/loanApplications/types";
import FormikAutocomplete from "components/forms/FormikAutocomplete";
import FormikAutocompleteMultiple from "components/forms/FormikAutocompleteMultiple";
import FormikDatePicker from "components/forms/FormikDatePicker";
import FormikNumberField from "components/forms/FormikNumberField";
import FormSection from "components/forms/FormSection";
import UltimateButton from "components/UltimateButton";

import { loanAccountTypeFormatter } from "../formatters/loanAccountTypeFormatter";
import { loanStatusFormatter } from "../formatters/loanStatusFormatter.tsx";

export type LoanApplicationFormValues = {
  _id: string;
  lenderId: string | null;
  loanAmount: number | null;
  monthlyPayment: number | null;
  term: number | null;
  creditInquiry: string[];
  applicationDate: Date | string | null;
  status: string | null;
  accountType: string;
  interestRate: number | null;
  originationFee: number | null;
  reasonCode: string | null;
};

interface ILoanApplicationForm {
  handleSubmit: (values: LoanApplicationFormValues) => void;
  handleClose: () => void;
  initialValues?: LoanApplicationFormValues;
  submitStatus?: MutationStatus;
  edit?: boolean;
}

const creditInquiryOptions = [
  { label: "Experian", value: LoanApplicationCreditInquiry.EXPERIAN },
  { label: "Equifax", value: LoanApplicationCreditInquiry.EQUIFAX },
  {
    label: "TransUnion",
    value: LoanApplicationCreditInquiry.TRANSUNION,
  },
  { label: "Tri-Merge", value: LoanApplicationCreditInquiry.TRI_MERGE },
  { label: "Soft Pull", value: LoanApplicationCreditInquiry.SOFT_PULL },
];

const loanApplicationStatusOptions = Object.values(LoanApplicationStatus).map(
  (value) => ({
    value,
    label: loanStatusFormatter(value as LoanApplicationStatus),
  }),
);

const accountTypeOptions = [
  LoanApplicationAccountType.UNSECURED_LOAN_INDIVIDUAL,
  LoanApplicationAccountType.UNSECURED_LOAN_JOINT,
  LoanApplicationAccountType.BUSINESS_LOAN_PERSONALLY_GUARANTEED,
  LoanApplicationAccountType.BUSINESS_LOAN_PERSONALLY_GUARANTEED_JOINT,
  LoanApplicationAccountType.HELOC,
  LoanApplicationAccountType.SBA_7A,
  LoanApplicationAccountType.SBA_504,
].map((value) => ({
  value,
  label: loanAccountTypeFormatter(value),
}));

const reasonCodeOptions = [
  "Home Improvement",
  "Large Purchase",
  "Debt Consolidation",
  "Business Acquisition",
  "Business Start Up",
  "Business",
].map((value) => ({
  value,
  label: value,
}));

const now = dayjs().toString();

const LoanApplicationForm: FC<ILoanApplicationForm> = ({
  handleClose,
  handleSubmit,
  initialValues = {
    _id: "",
    lenderId: null,
    loanAmount: null,
    monthlyPayment: null,
    term: null,
    creditInquiry: [],
    // applicationDate: null,
    applicationDate: now,
    status: LoanApplicationStatus.APPLICATION_PENDING_SUBMISSION,
    accountType: "",
    interestRate: null,
    originationFee: null,
    reasonCode: null,
  },
  submitStatus,
}) => {
  const schema = yup.object({
    lenderId: yup.string().required().nullable(),
    loanAmount: yup
      .number()
      // .required()
      .nullable(),
    monthlyPayment: yup
      .number()
      // .required()
      .nullable(),
    term: yup
      .number()
      // .required()
      .nullable(),
    creditInquiry: yup
      .array()
      // .required()
      .nullable(),
    applicationDate: yup.date().required().nullable(),
    status: yup.string().required().nullable(),
    accountType: yup.string().required().nullable(),
    interestRate: yup
      .number()
      // .required()
      .nullable(),
    // originationFee: yup.number(),
    reasonCode: yup.string().required().nullable(),
  });

  const formik = useFormik({
    initialValues,
    validationSchema: schema,
    onSubmit: handleSubmit,
    enableReinitialize: true,
  });

  const { setFieldValue } = formik;

  const { data: lenders, isLoading: areLendersLoading } = useQuery(
    hubspotKeys.listLenders(),
    async () => {
      const { data: res } = await getHubspotLenders();
      return res.data;
    },
  );

  const lendersOptions = useMemo(() => {
    if (lenders?.length) {
      return lenders
        .map((lender) => ({
          value: lender.id,
          label: lender.lender_name,
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
    }

    return [];
  }, [lenders]);

  return (
    <FormikProvider value={formik}>
      <FormikForm>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            <FormSection
              title="Loan Application"
              description="Lender, amount, terms, and application details."
              Icon={Briefcase}
            >
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormikAutocomplete
                    options={lendersOptions}
                    name="lenderId"
                    label="Lender"
                    loading={areLendersLoading}
                    onChange={async (e, target) => {
                      const lenderId = target?.value;

                      await setFieldValue("lenderId", lenderId);
                      await setFieldValue("originationFee", "");
                      await setFieldValue("creditInquiry", []);

                      const lender = lenders?.find(
                        (singleLender) => singleLender.id === lenderId,
                      );
                      if (lender?.origination_fee) {
                        await setFieldValue(
                          "originationFee",
                          parseFloat(lender?.origination_fee),
                        );
                      }
                      if (lender?.credit_inquiry) {
                        const creditBureaus =
                          lender.credit_inquiry
                            .split(";")
                            .map(
                              (creditBureau) =>
                                HubspotLenderCreditBureauEnum[
                                  creditBureau as keyof typeof HubspotLenderCreditBureauEnum
                                ],
                            ) ?? [];
                        setFieldValue("creditInquiry", creditBureaus);
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} lg={6}>
                  <FormikNumberField
                    name="loanAmount"
                    label="Loan Amount"
                    prefix="$"
                  />
                </Grid>
                <Grid item xs={12} lg={6}>
                  <FormikNumberField
                    name="monthlyPayment"
                    label="Monthly Payment"
                    prefix="$"
                  />
                </Grid>
                <Grid item xs={12} lg={6}>
                  <FormikNumberField name="term" label="Term" />
                </Grid>
                <Grid item xs={12} lg={6}>
                  <FormikAutocompleteMultiple
                    name="creditInquiry"
                    label="Credit Inquiry"
                    options={creditInquiryOptions}
                  />
                </Grid>
                <Grid item xs={12} lg={6}>
                  <FormikDatePicker
                    name="applicationDate"
                    label="Application Date"
                    timezone="UTC"
                  />
                </Grid>
                <Grid item xs={12} lg={6}>
                  <FormikAutocomplete
                    options={loanApplicationStatusOptions}
                    name="status"
                    label="Status"
                  />
                </Grid>
                <Grid item xs={12} lg={6}>
                  <FormikAutocomplete
                    options={accountTypeOptions}
                    name="accountType"
                    label="Account Type"
                  />
                </Grid>
                <Grid item xs={12} lg={6}>
                  <FormikNumberField
                    name="interestRate"
                    label="Interest Rate"
                    suffix="%"
                  />
                </Grid>
                <Grid item xs={12} lg={6}>
                  <FormikNumberField
                    name="originationFee"
                    label="Origination Fee Amount"
                    prefix="$"
                  />
                </Grid>
                <Grid item xs={12} lg={6}>
                  <FormikAutocomplete
                    options={reasonCodeOptions}
                    name="reasonCode"
                    label="Reason Code"
                  />
                </Grid>
              </Grid>
            </FormSection>
          </Stack>
        </DialogContent>
        <DialogActions>
          <UltimateButton
            type="submit"
            text="Save"
            Icon={
              Save as React.ComponentType<{
                size?: number;
                className?: string;
                style?: React.CSSProperties;
              }>
            }
            loading={submitStatus === "loading"}
            sx={{
              minWidth: 150,
              py: 1.5,
            }}
          />
        </DialogActions>
      </FormikForm>
    </FormikProvider>
  );
};

export default LoanApplicationForm;
